import React, { useState } from 'react';
import { MatchDetails } from '../../../shared/services/matchDetails';
import { useTournaments } from '../../../shared/hooks/useTournaments';
import { useOfficials } from '../../../shared/hooks/useOfficials';

interface MatchDetailsTabProps {
  matchDetails: MatchDetails;
  onUpdate?: (updates: Partial<MatchDetails>) => void;
  onOfficialsUpdate?: (role: string, officialId: string) => void;
}

const MatchDetailsTab: React.FC<MatchDetailsTabProps> = ({ matchDetails, onUpdate, onOfficialsUpdate }) => {
  const { tournaments } = useTournaments();
  const { officials } = useOfficials();

  // Local state for form values
  const [formData] = useState({
    date: matchDetails.start_date ? new Date(matchDetails.start_date).toISOString().split('T')[0] : '',
    time: matchDetails.start_date ? new Date(matchDetails.start_date).toTimeString().slice(0, 5) : '',
    tournament_id: matchDetails.tournament?.id || '',
    name: matchDetails.name || '',
    venue: matchDetails.venue || '',
    end_time: matchDetails.end_date ? new Date(matchDetails.end_date).toTimeString().slice(0, 5) : '',
    officials: {
      referee: matchDetails.match_officials?.find(o => o.role === 'referee')?.official_id || '',
      assistant1: matchDetails.match_officials?.find(o => o.role === 'assistant1')?.official_id || '',
      assistant2: matchDetails.match_officials?.find(o => o.role === 'assistant2')?.official_id || '',
      var: matchDetails.match_officials?.find(o => o.role === 'var')?.official_id || '',
      var_assistant: matchDetails.match_officials?.find(o => o.role === 'var_assistant')?.official_id || '',
      fourth: matchDetails.match_officials?.find(o => o.role === 'fourth')?.official_id || ''
    }
  });




  // Helper function to get tournament name
  const getTournamentName = (tournamentId: string) => {
    const tournament = tournaments.find(t => t.id === tournamentId);
    return tournament ? tournament.name : '-';
  };

  // Helper function to get official name
  const getOfficialName = (officialId: string) => {
    const official = officials.find(o => o.id === officialId);
    return official ? official.name : '-';
  };


  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Match Information Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
            <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-900">Match Information</h3>
        </div>
        
        <div className="space-y-3">
          {/* Row 1 - Match No. */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Match No.</span>
            <span className="text-xs font-semibold text-gray-900">{formData.name || '-'}</span>
          </div>
          
          {/* Row 2 - Tournament */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Tournament</span>
            <span className="text-xs font-semibold text-gray-900">{getTournamentName(formData.tournament_id)}</span>
          </div>
          
          {/* Row 3 - Start Time */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Start Time</span>
            <span className="text-xs font-semibold text-gray-900">{formData.time || '-'}</span>
          </div>
          
          {/* Row 4 - Date */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Date</span>
            <span className="text-xs font-semibold text-gray-900">{formData.date || '-'}</span>
          </div>
          
          {/* Row 5 - Venue */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Venue</span>
            <span className="text-xs font-semibold text-gray-900">{formData.venue || '-'}</span>
          </div>
          
          {/* Row 6 - End Time */}
          <div className="flex items-center justify-between py-2">
            <span className="text-xs font-medium text-gray-600">End Time</span>
            <span className="text-xs font-semibold text-gray-900">{formData.end_time || '-'}</span>
          </div>
        </div>
      </div>

      {/* Match Officials Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-6 h-6 bg-green-100 rounded-md flex items-center justify-center">
            <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-900">Match Officials</h3>
        </div>
        
        <div className="space-y-3">
          {/* Row 1 */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Ref</span>
            <span className="text-xs font-semibold text-gray-900">{getOfficialName(formData.officials.referee)}</span>
          </div>
          
          {/* Row 2 */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Assistant Ref #1</span>
            <span className="text-xs font-semibold text-gray-900">{getOfficialName(formData.officials.assistant1)}</span>
          </div>
          
          {/* Row 3 */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">Assistant Ref #2</span>
            <span className="text-xs font-semibold text-gray-900">{getOfficialName(formData.officials.assistant2)}</span>
          </div>
          
          {/* Row 4 */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">VAR</span>
            <span className="text-xs font-semibold text-gray-900">{getOfficialName(formData.officials.var)}</span>
          </div>
          
          {/* Row 5 */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-600">VAR Assistant</span>
            <span className="text-xs font-semibold text-gray-900">{getOfficialName(formData.officials.var_assistant)}</span>
          </div>
          
          {/* Row 6 */}
          <div className="flex items-center justify-between py-2">
            <span className="text-xs font-medium text-gray-600">Fourth Official</span>
            <span className="text-xs font-semibold text-gray-900">{getOfficialName(formData.officials.fourth)}</span>
          </div>
        </div>
      </div>

      {/* Score Summary Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-6 h-6 bg-purple-100 rounded-md flex items-center justify-center">
            <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-900">Score Summary</h3>
        </div>
        
        <div className="space-y-4">
          {/* Score Section with Teams */}
          <div className="bg-gradient-to-r from-blue-50 to-red-50 rounded-lg p-4">
            <div className="flex items-center justify-between min-h-[40px]">
              {/* Home Team */}
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                {matchDetails.home_team?.logo && (
                  <img 
                    src={matchDetails.home_team.logo} 
                    alt={`${matchDetails.home_team.name} logo`}
                    className="w-6 h-6 object-contain flex-shrink-0"
                  />
                )}
                <div className="text-sm font-semibold text-blue-600 truncate text-right">
                  {matchDetails.home_team?.name || 'Home Team'}
                </div>
              </div>
              
              {/* Score */}
              <div className="text-2xl font-bold text-gray-900 flex-shrink-0 px-4 whitespace-nowrap">
                {matchDetails.home_score || 0} - {matchDetails.away_score || 0}
              </div>
              
              {/* Away Team */}
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <div className="text-sm font-semibold text-red-600 truncate">
                  {matchDetails.away_team?.name || 'Away Team'}
                </div>
                {matchDetails.away_team?.logo && (
                  <img 
                    src={matchDetails.away_team.logo} 
                    alt={`${matchDetails.away_team.name} logo`}
                    className="w-6 h-6 object-contain flex-shrink-0"
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Goal Scorers Section */}
          <div className="grid grid-cols-2 gap-4">
            {/* Home Team Scorers */}
            <div className="bg-white rounded-lg p-3 min-h-[60px] border border-gray-200">
              <div className="text-xs font-medium text-blue-600 mb-2 text-center">
                {matchDetails.home_team?.name || 'Home Team'} Scorers
              </div>
              <div className="text-xs text-gray-500 text-center">
                No goal scorers available
              </div>
            </div>
            
            {/* Away Team Scorers */}
            <div className="bg-white rounded-lg p-3 min-h-[60px] border border-gray-200">
              <div className="text-xs font-medium text-red-600 mb-2 text-center">
                {matchDetails.away_team?.name || 'Away Team'} Scorers
              </div>
              <div className="text-xs text-gray-500 text-center">
                No goal scorers available
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchDetailsTab;
